import React, { useEffect, useRef } from "react";
import Slider from "react-slick";
import ModalVideo from "react-modal-video";

const ProjectDetailsModal = ({ projectDetails, setIsOpen }) => {
  const sliderRef = useRef();

  const types = {
    IMAGE: "image",
    VIDEO: "video",
    DOCUMENT: "document",
    CASESTUDY: "casestudy",
  };

  const SlickArrowLeft = ({ currentSlide, slideCount, ...props }) => (
    <button
      {...props}
      className={
        "slick-prev slick-arrow" + (currentSlide === 0 ? " slick-disabled" : "")
      }
      aria-hidden="true"
      aria-disabled={currentSlide === 0}
      type="button"
    >
      <i className="fa fa-chevron-left"></i>
    </button>
  );

  const SlickArrowRight = ({ currentSlide, slideCount, ...props }) => (
    <button
      {...props}
      className={
        "slick-next slick-arrow" +
        (currentSlide === slideCount - 1 ? " slick-disabled" : "")
      }
      aria-hidden="true"
      aria-disabled={currentSlide === slideCount - 1}
      type="button"
    >
      <i className="fa fa-chevron-right"></i>
    </button>
  );

  const sliderSettings = {
    dots: true,
    arrows: true,
    prevArrow: <SlickArrowLeft />,
    nextArrow: <SlickArrowRight />,
    infinite: true,
    adaptiveHeight: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4500,
  };

  useEffect(() => {
    if (projectDetails?.type === types.DOCUMENT && sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
  }, [projectDetails, types.DOCUMENT]);

  /* ─── Styles scoped to the case study modal ─── */
  const cs = {
    sectionTitle: {
      fontSize: "11px",
      fontWeight: "700",
      letterSpacing: "2px",
      textTransform: "uppercase",
      color: "#888",
      marginBottom: "12px",
      marginTop: "32px",
    },
    metaRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "24px",
      marginBottom: "24px",
      padding: "16px 20px",
      background: "#f8f8f8",
      borderLeft: "3px solid #000",
    },
    metaItem: {
      minWidth: "160px",
    },
    metaLabel: {
      fontSize: "10px",
      fontWeight: "700",
      letterSpacing: "1.5px",
      textTransform: "uppercase",
      color: "#aaa",
      marginBottom: "2px",
    },
    metaValue: {
      fontSize: "13px",
      fontWeight: "600",
      color: "#111",
    },
    phaseCard: {
      display: "flex",
      gap: "16px",
      padding: "18px 20px",
      marginBottom: "12px",
      background: "#fff",
      border: "1px solid #e8e8e8",
      borderRadius: "2px",
    },
    phaseNumber: {
      fontSize: "22px",
      fontWeight: "800",
      color: "#e0e0e0",
      lineHeight: "1",
      minWidth: "32px",
    },
    phaseIcon: {
      width: "36px",
      height: "36px",
      background: "#000",
      color: "#fff",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
      flexShrink: 0,
    },
    phaseTitle: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#111",
      marginBottom: "6px",
    },
    phaseDesc: {
      fontSize: "13px",
      color: "#555",
      lineHeight: "1.6",
      margin: 0,
    },
    entityRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      padding: "8px 0",
      borderBottom: "1px solid #f0f0f0",
    },
    entityName: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#111",
      minWidth: "120px",
    },
    entityNote: {
      fontSize: "12px",
      color: "#666",
    },
    deliverableItem: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "6px 0",
      fontSize: "13px",
      color: "#333",
    },
    checkIcon: {
      width: "18px",
      height: "18px",
      background: "#000",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: "#fff",
      fontSize: "9px",
    },
    valueCard: {
      padding: "18px 20px",
      background: "#f8f8f8",
      borderTop: "3px solid #000",
      marginBottom: "12px",
    },
    valueTitle: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#111",
      marginBottom: "6px",
    },
    valueBody: {
      fontSize: "12px",
      color: "#555",
      lineHeight: "1.6",
      margin: 0,
    },
    divider: {
      height: "1px",
      background: "#e8e8e8",
      margin: "28px 0",
    },
  };

  return (
    <>
      {/* ═══════════════════════════════════════════
          CASE STUDY MODAL
      ═══════════════════════════════════════════ */}
      {projectDetails?.type === types.CASESTUDY && (
        <div className="project-details-modal">
          <div
            className="modal fade bg-dark-1 show"
            style={{ display: "block" }}
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
          >
            <div
              className="modal-dialog modal-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content rounded-0">
                <div className="modal-body" style={{ padding: "40px" }}>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsOpen(false)}
                  />

                  {/* Header */}
                  <div style={{ marginBottom: "8px" }}>
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: "700",
                        letterSpacing: "2px",
                        textTransform: "uppercase",
                        color: "#888",
                      }}
                    >
                      Case Study
                    </span>
                  </div>
                  <h2
                    style={{
                      fontSize: "26px",
                      fontWeight: "800",
                      color: "#000",
                      marginBottom: "4px",
                    }}
                  >
                    {projectDetails.title}
                  </h2>
                  {projectDetails.subtitle && (
                    <p style={{ fontSize: "14px", color: "#888", marginBottom: "28px" }}>
                      {projectDetails.subtitle}
                    </p>
                  )}

                  {/* Meta bar */}
                  <div style={cs.metaRow}>
                    <div style={cs.metaItem}>
                      <div style={cs.metaLabel}>Client</div>
                      <div style={cs.metaValue}>{projectDetails.caseStudy.client}</div>
                    </div>
                    <div style={cs.metaItem}>
                      <div style={cs.metaLabel}>My Role</div>
                      <div style={cs.metaValue}>{projectDetails.caseStudy.role}</div>
                    </div>
                    <div style={cs.metaItem}>
                      <div style={cs.metaLabel}>Date</div>
                      <div style={cs.metaValue}>{projectDetails.caseStudy.date}</div>
                    </div>
                    <div style={{ ...cs.metaItem, minWidth: "240px" }}>
                      <div style={cs.metaLabel}>Stack</div>
                      <div style={cs.metaValue}>{projectDetails.caseStudy.technologies}</div>
                    </div>
                  </div>

                  {/* Brief */}
                  <p style={cs.sectionTitle}>The Brief</p>
                  <p style={{ fontSize: "14px", color: "#444", lineHeight: "1.7" }}>
                    {projectDetails.caseStudy.brief}
                  </p>

                  {/* Goal */}
                  <p style={cs.sectionTitle}>Goal & Approach</p>
                  <p style={{ fontSize: "14px", color: "#444", lineHeight: "1.7" }}>
                    {projectDetails.caseStudy.goal}
                  </p>

                  <div style={cs.divider} />

                  {/* Design Process Phases */}
                  <p style={cs.sectionTitle}>Design & Development Process</p>
                  {projectDetails.caseStudy.phases.map((phase, i) => (
                    <div key={i} style={cs.phaseCard}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                        <div style={cs.phaseNumber}>{phase.number}</div>
                        <div style={cs.phaseIcon}>
                          <i className={phase.icon}></i>
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={cs.phaseTitle}>{phase.title}</div>
                        <p style={cs.phaseDesc}>{phase.description}</p>
                      </div>
                    </div>
                  ))}

                  <div style={cs.divider} />

                  {/* Architecture */}
                  <div className="row g-4">
                    <div className="col-md-6">
                      <p style={cs.sectionTitle}>Architecture</p>
                      <div style={{ marginBottom: "16px" }}>
                        <div style={cs.metaLabel}>Stack</div>
                        <div style={{ fontSize: "13px", color: "#333", marginTop: "4px" }}>
                          {projectDetails.caseStudy.architecture.stack}
                        </div>
                      </div>
                      <div style={{ marginBottom: "16px" }}>
                        <div style={cs.metaLabel}>Authentication</div>
                        <div style={{ fontSize: "13px", color: "#333", marginTop: "4px" }}>
                          {projectDetails.caseStudy.architecture.auth}
                        </div>
                      </div>
                      <div>
                        <div style={cs.metaLabel}>
                          Database — {projectDetails.caseStudy.architecture.entities}
                        </div>
                        <div style={{ marginTop: "8px" }}>
                          {projectDetails.caseStudy.architecture.keyEntities.map((entity, i) => (
                            <div key={i} style={cs.entityRow}>
                              <span style={cs.entityName}>{entity.name}</span>
                              <span style={cs.entityNote}>{entity.note}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Deliverables */}
                    <div className="col-md-6">
                      <p style={cs.sectionTitle}>Deliverables</p>
                      {projectDetails.caseStudy.deliverables.map((item, i) => (
                        <div key={i} style={cs.deliverableItem}>
                          <div style={cs.checkIcon}>
                            <i className="fas fa-check"></i>
                          </div>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={cs.divider} />

                  {/* Value Proposition */}
                  <p style={cs.sectionTitle}>Value Delivered</p>
                  <div className="row g-3">
                    {projectDetails.caseStudy.valueProposition.map((vp, i) => (
                      <div key={i} className="col-md-4">
                        <div style={cs.valueCard}>
                          <div style={cs.valueTitle}>{vp.title}</div>
                          <p style={cs.valueBody}>{vp.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          DOCUMENT MODAL (unchanged)
      ═══════════════════════════════════════════ */}
      {projectDetails?.type === types.DOCUMENT && (
        <div className="project-details-modal">
          <div
            className="modal fade bg-dark-1 show"
            style={{ display: "block" }}
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
          >
            <div
              className="modal-dialog modal-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content rounded-0">
                <div className="modal-body">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsOpen(false)}
                  />
                  <div className="container ajax-container">
                    <h2 className="text-6 fw-600 text-center mb-4">
                      {projectDetails?.title}
                    </h2>
                    <div className="row g-4">
                      <div className="col-md-7">
                        <Slider {...sliderSettings} ref={sliderRef}>
                          <div className="item">
                            <img
                              className="img-fluid"
                              alt=""
                              src={projectDetails?.thumbImage}
                            />
                          </div>
                          {projectDetails?.document?.sliderImages?.map(
                            (image, index) => (
                              <div className="item" key={index}>
                                <img className="img-fluid" alt="" src={image} />
                              </div>
                            )
                          )}
                        </Slider>
                      </div>
                      <div className="col-md-5">
                        <h4 className="text-4 fw-600">Project Info:</h4>
                        <p>{projectDetails?.document?.projectInfo}</p>
                        <h4 className="text-4 fw-600 mt-4">Project Details:</h4>
                        <ul className="list-style-2">
                          <li>
                            <span className="text-dark fw-600 me-2">Client:</span>
                            {projectDetails?.document?.client}
                          </li>
                          <li>
                            <span className="text-dark fw-600 me-2">Technologies:</span>
                            {projectDetails?.document?.technologies}
                          </li>
                          <li>
                            <span className="text-dark fw-600 me-2">Industry:</span>
                            {projectDetails?.document?.industry}
                          </li>
                          <li>
                            <span className="text-dark fw-600 me-2">Date:</span>
                            {projectDetails?.document?.date}
                          </li>
                          <li>
                            <span className="text-dark fw-600 me-2">URL:</span>
                            <a
                              href={projectDetails?.document?.url?.link}
                              className="btn btn-primary shadow-none rounded-0 px-3 py-1"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {projectDetails?.document?.url?.name}
                              <i className="fas fa-external-link-alt ms-1" />
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          IMAGE MODAL (unchanged)
      ═══════════════════════════════════════════ */}
      {projectDetails?.type === types.IMAGE && (
        <div className="portfolio-image-modal" onClick={() => setIsOpen(false)}>
          <img
            src={projectDetails?.thumbImage}
            alt={projectDetails?.title}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="btn-close btn-close-white opacity-10"
            onClick={() => setIsOpen(false)}
          />
        </div>
      )}

      {/* ═══════════════════════════════════════════
          VIDEO MODAL (unchanged)
      ═══════════════════════════════════════════ */}
      {projectDetails?.type === types.VIDEO && (
        <ModalVideo
          channel={projectDetails?.video?.vimeo ? "vimeo" : "youtube"}
          autoplay
          isOpen={projectDetails?.type === types.VIDEO}
          videoId={projectDetails?.video?.id}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default ProjectDetailsModal;
